import { useMemo, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { BarChart3, Presentation } from 'lucide-react';
import { xamplinApi, deriveEventStatus, PURPOSE_BY_ID } from '@/lib/api/xamplin';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { CountUp } from '@/components/ui/CountUp';
import { RecapDeck, type RecapData } from './RecapDeck';

/**
 * Insights (build-v2 F8) — cross-cut overview built from REAL migrated data:
 * asset distribution by Purpose, events by project, event lifecycle split.
 * MENT / trial-validation funnel / DiD require CS-1 survey responses (not yet
 * migrated) and are shown as honest pending states.
 */
const PURPOSE_COLOR: Record<string, string> = {
  SEE: '#5aa9ff', ENGAGE: '#f4b740', TRY: '#4dd28b', ACT: '#ff5a4f',
};

export function InsightsOverviewPage() {
  const selectedBrandId = useSelectedBrandId();
  const brandsQ = useQuery({ queryKey: queryKeys.brands.all, queryFn: () => brandsApi.list() });
  const projectsQ = useQuery({
    queryKey: queryKeys.xamplin.projects(),
    queryFn: () => xamplinApi.projects.list(),
  });
  const assetsQ = useQuery({
    queryKey: queryKeys.xamplin.assets,
    queryFn: () => xamplinApi.assets.list(300),
  });

  const brands = (brandsQ.data?.items ?? []).filter((b) => b.companyType !== 'Agency');
  const activeBrandId = selectedBrandId ?? brands[0]?.id ?? null;
  const projects = useMemo(
    () => (projectsQ.data?.items ?? []).filter((p) => p.companyId === activeBrandId),
    [projectsQ.data, activeBrandId],
  );

  const structureQs = useQueries({
    queries: projects.map((p) => ({
      queryKey: queryKeys.xamplin.projectStructure(p.id),
      queryFn: () => xamplinApi.projects.structure(p.id),
    })),
  });

  // Aggregate events across the brand's projects.
  const byProject = projects.map((p, i) => {
    const groups = structureQs[i].data?.structured?.project?.eventGroups ?? [];
    const events = groups.flatMap((g) => g.events ?? []);
    return { project: p, groups: groups.length, events };
  });
  const allEvents = byProject.flatMap((x) => x.events);
  const statusSplit = { plan: 0, live: 0, completed: 0 };
  for (const e of allEvents) statusSplit[deriveEventStatus(e)]++;
  const totalAttendance = allEvents.reduce((s, e) => s + (e.attendance ?? 0), 0);

  // Measure (MENT + CS-1 funnel + demographics) aggregated across projects.
  const measureQs = useQueries({
    queries: projects.map((p) => ({
      queryKey: queryKeys.xamplin.measure(p.id),
      queryFn: () => xamplinApi.projectMeasure(p.id),
    })),
  });
  const funnel = { scanned: 0, started: 0, tried: 0, enjoyed: 0, shifted: 0 };
  let mentSum = 0;
  let mentW = 0;
  let responsesTotal = 0;
  const genderAgg: Record<string, number> = {};
  const ageAgg: Record<string, number> = {};
  // DiD (treated vs control proxy) + stakeholder validation accumulators.
  let didTreatedShift = 0;
  let didTreatedN = 0;
  let didControlShift = 0;
  let didControlN = 0;
  let stakeTotal = 0;
  let stakeAligned = 0;
  let stakeRatingSum = 0;
  for (const mq of measureQs) {
    const m = mq.data;
    if (!m) continue;
    funnel.scanned += m.funnel.scanned;
    funnel.started += m.funnel.started;
    funnel.tried += m.funnel.tried;
    funnel.enjoyed += m.funnel.enjoyed;
    funnel.shifted += m.funnel.shifted;
    mentSum += m.ment * m.responses;
    mentW += m.responses;
    responsesTotal += m.responses;
    for (const g of m.demographics.gender) genderAgg[g.label] = (genderAgg[g.label] ?? 0) + g.n;
    for (const a of m.demographics.age) ageAgg[a.label] = (ageAgg[a.label] ?? 0) + a.n;
    if (m.did) {
      didTreatedShift += (m.did.treatedRate / 100) * m.did.treatedN;
      didTreatedN += m.did.treatedN;
      didControlShift += (m.did.controlRate / 100) * m.did.controlN;
      didControlN += m.did.controlN;
    }
    if (m.stakeholder) {
      stakeTotal += m.stakeholder.total;
      stakeAligned += m.stakeholder.aligned;
      stakeRatingSum += m.stakeholder.avgRating * m.stakeholder.total;
    }
  }
  const didTreatedRate = didTreatedN ? (didTreatedShift / didTreatedN) * 100 : 0;
  const didControlRate = didControlN ? (didControlShift / didControlN) * 100 : 0;
  const didLiftPp = Number((didTreatedRate - didControlRate).toFixed(1));
  const stakeAlignedPct = stakeTotal ? Math.round((stakeAligned / stakeTotal) * 100) : 0;
  const stakeAvgRating = stakeTotal ? Number((stakeRatingSum / stakeTotal).toFixed(2)) : 0;
  const mentAvg = mentW ? Number((mentSum / mentW).toFixed(1)) : 0;
  const responseRate = funnel.scanned ? Math.round((funnel.started / funnel.scanned) * 100) : 0;
  const FUNNEL_STAGES = [
    ['QR scanned', funnel.scanned],
    ['Started', funnel.started],
    ['Tried', funnel.tried],
    ['Enjoyed', funnel.enjoyed],
    ['Pref. shift', funnel.shifted],
  ] as const;
  const genderTotal = Object.values(genderAgg).reduce((s, n) => s + n, 0) || 1;
  const ageTotal = Object.values(ageAgg).reduce((s, n) => s + n, 0) || 1;
  const AGE_ORDER = ['21-24', '25-34', '35-44', '45+'];

  // Assets by Purpose (real distribution).
  const assets = assetsQ.data?.items ?? [];
  const purposeCounts = { SEE: 0, ENGAGE: 0, TRY: 0, ACT: 0 } as Record<string, number>;
  for (const a of assets) {
    const p = a.assetObjetive ? PURPOSE_BY_ID[a.assetObjetive] : null;
    if (p) purposeCounts[p]++;
  }
  const purposeTotal = Object.values(purposeCounts).reduce((s, n) => s + n, 0) || 1;

  const loading = projectsQ.isLoading || structureQs.some((q) => q.isLoading);

  const [deckOpen, setDeckOpen] = useState(false);
  const activeBrandName = brands.find((b) => b.id === activeBrandId)?.name ?? 'Brand';
  const recapData: RecapData = {
    brandName: activeBrandName,
    dateLabel: new Date().toLocaleDateString('en', { month: 'long', year: 'numeric' }),
    projects: projects.length,
    events: allEvents.length,
    reach: totalAttendance,
    responses: responsesTotal,
    ment: mentAvg,
    responseRate,
    funnel,
    gender: Object.entries(genderAgg).map(([label, n]) => ({ label, pct: Math.round((n / genderTotal) * 100) })),
    age: AGE_ORDER.map((label) => ({ label, pct: Math.round(((ageAgg[label] ?? 0) / ageTotal) * 100) })),
    did: { treatedRate: didTreatedRate, controlRate: didControlRate, liftPp: didLiftPp },
    stakeholder: { alignedPct: stakeAlignedPct, avgRating: stakeAvgRating },
    byMarket: projects.map((p, i) => {
      const m = measureQs[i]?.data;
      const reach = byProject[i].events.reduce((s, e) => s + (e.attendance ?? 0), 0);
      return { name: p.name, ment: m?.ment ?? 0, responses: m?.responses ?? 0, reach };
    }),
  };

  return (
    <div className="page flex flex-col gap-6">
      {deckOpen && <RecapDeck data={recapData} onClose={() => setDeckOpen(false)} />}
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
          <BarChart3 className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Insights
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {projects.length} projects · {allEvents.length} events
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDeckOpen(true)}
          className="ml-auto flex items-center gap-2 rounded-lg bg-[var(--color-xk-accent)] px-3 py-2 text-sm font-bold text-white hover:opacity-90"
        >
          <Presentation className="h-4 w-4" /> Recap deck
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Projects" value={String(projects.length)} />
        <Kpi label="Events" value={String(allEvents.length)} />
        <Kpi label="Reach · foot-traffic" value={<CountUp value={totalAttendance} />} tone="#4dd28b" />
        <Kpi label="Assets captured" value={assets.length.toLocaleString()} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Assets by Purpose — real */}
        <Panel title="Assets by Purpose" sub="distribución real">
          <div className="flex flex-col gap-2.5">
            {(['SEE', 'ENGAGE', 'TRY', 'ACT'] as const).map((p) => {
              const n = purposeCounts[p];
              const pct = Math.round((n / purposeTotal) * 100);
              return (
                <div key={p} className="flex items-center gap-3">
                  <span className="mono w-14 text-[11px] font-bold" style={{ color: PURPOSE_COLOR[p] }}>
                    {p}
                  </span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PURPOSE_COLOR[p] }} />
                  </div>
                  <span className="mono w-16 text-right text-[11px] text-[var(--color-xk-text-muted)]">
                    {n} · {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Events by project — real */}
        <Panel title="Events by project" sub={loading ? 'cargando…' : 'real'}>
          <div className="flex flex-col gap-2.5">
            {byProject
              .slice()
              .sort((a, b) => b.events.length - a.events.length)
              .map((x) => {
                const max = Math.max(1, ...byProject.map((y) => y.events.length));
                const pct = Math.round((x.events.length / max) * 100);
                return (
                  <div key={x.project.id} className="flex items-center gap-3">
                    <span className="w-32 truncate text-[11px] text-[var(--color-xk-lavender)]">
                      {x.project.name}
                    </span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                      <div className="h-full rounded-full bg-[var(--color-xk-accent)]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="mono w-8 text-right text-[11px] text-[var(--color-xk-text-muted)]">
                      {x.events.length}
                    </span>
                  </div>
                );
              })}
          </div>
        </Panel>

        {/* Lifecycle split — real (derived) */}
        <Panel title="Event lifecycle" sub="Plan / Track / Measure">
          <div className="grid grid-cols-3 gap-3">
            {([['Plan', statusSplit.plan, 'var(--color-xk-accent)'], ['Track', statusSplit.live, '#4dd28b'], ['Measure', statusSplit.completed, '#5aa9ff']] as const).map(
              ([label, n, color]) => (
                <div key={label} className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] p-3 text-center">
                  <div className="font-display text-2xl font-extrabold" style={{ color }}>{n}</div>
                  <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">{label}</div>
                </div>
              ),
            )}
          </div>
        </Panel>

        {/* Trial-validation funnel + MENT (from CS-1 responses) */}
        <Panel title="Trial validation · MENT" sub={`${responsesTotal.toLocaleString()} CS-1`}>
          <div className="mb-3 flex items-end gap-4">
            <div>
              <div className="font-display text-4xl font-extrabold text-[var(--color-xk-accent)]">
                {mentAvg || '—'}
              </div>
              <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
                MENT score · /10
              </div>
            </div>
            <div className="mono text-[11px] text-[var(--color-xk-text-muted)]">
              Response rate {responseRate}%
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {FUNNEL_STAGES.map(([label, n]) => {
              const pct = funnel.scanned ? Math.round((n / funnel.scanned) * 100) : 0;
              return (
                <div key={label} className="flex items-center gap-3">
                  <span className="w-24 text-[11px] text-[var(--color-xk-lavender)]">{label}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                    <div className="h-full rounded-full bg-[var(--color-xk-accent)]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="mono w-20 text-right text-[11px] text-[var(--color-xk-text-muted)]">
                    {n.toLocaleString()} · {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </Panel>

        {/* Demographics (CS-1) */}
        <Panel title="Demographics" sub="CS-1 · Mujer-first">
          <div className="flex flex-col gap-2.5">
            {Object.entries(genderAgg)
              .sort((a, b) => (a[0] === 'Mujer' ? -1 : b[0] === 'Mujer' ? 1 : b[1] - a[1]))
              .map(([label, n]) => {
                const pct = Math.round((n / genderTotal) * 100);
                const color = label === 'Mujer' ? '#B262F5' : label === 'Hombre' ? '#5aa9ff' : '#f4b740';
                return (
                  <div key={label} className="flex items-center gap-3">
                    <span className="w-16 text-[11px] text-[var(--color-xk-lavender)]">{label}</span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <span className="mono w-12 text-right text-[11px] text-[var(--color-xk-text-muted)]">{pct}%</span>
                  </div>
                );
              })}
            <div className="mt-2 grid grid-cols-4 gap-2">
              {AGE_ORDER.map((band) => {
                const pct = Math.round(((ageAgg[band] ?? 0) / ageTotal) * 100);
                return (
                  <div key={band} className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] p-2 text-center">
                    <div className="font-display text-sm font-bold text-[var(--color-xk-lavender)]">{pct}%</div>
                    <div className="mono text-[9px] text-[var(--color-xk-text-subtle)]">{band}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>

        {/* DiD — quasi difference-in-differences (opt-in vs control proxy) */}
        <Panel title="DiD · preference lift" sub="opt-in vs control proxy">
          {didTreatedN + didControlN === 0 ? (
            <p className="mono text-xs text-[var(--color-xk-text-muted)]">Sin datos de opt-in.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-end gap-2">
                <span
                  className="font-display text-3xl font-extrabold"
                  style={{ color: didLiftPp >= 0 ? '#4dd28b' : '#ff5a4f' }}
                >
                  {didLiftPp >= 0 ? '+' : ''}
                  {didLiftPp} pp
                </span>
                <span className="mono mb-1 text-[11px] text-[var(--color-xk-text-subtle)]">lift de preferencia</span>
              </div>
              {([
                ['Treated · opt-in', didTreatedRate, '#B262F5', didTreatedN],
                ['Control proxy', didControlRate, '#5aa9ff', didControlN],
              ] as const).map(([label, rate, color, n]) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="w-28 text-[11px] text-[var(--color-xk-lavender)]">{label}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, rate)}%`, background: color }} />
                  </div>
                  <span className="mono w-20 text-right text-[11px] text-[var(--color-xk-text-muted)]">
                    {rate.toFixed(1)}% · {n.toLocaleString()}
                  </span>
                </div>
              ))}
              <p className="mono text-[10px] leading-snug text-[var(--color-xk-text-subtle)]">
                Quasi-DiD: shift treated (opt-in) vs proxy de control (no-opt-in), medido post-trial.
                El panel pre/post real llega con la ola de follow-up de opt-in.
              </p>
            </div>
          )}
        </Panel>

        {/* Stakeholder validation — venue sign-off */}
        <Panel title="Stakeholder validation" sub={`${stakeTotal.toLocaleString()} sign-offs`}>
          {stakeTotal === 0 ? (
            <p className="mono text-xs text-[var(--color-xk-text-muted)]">Sin sign-offs aún.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex gap-6">
                <div>
                  <div className="font-display text-3xl font-extrabold text-[#4dd28b]">{stakeAlignedPct}%</div>
                  <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
                    coincidió con el brief
                  </div>
                </div>
                <div>
                  <div className="font-display text-3xl font-extrabold text-[var(--color-xk-lavender)]">
                    {stakeAvgRating}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
                    rating · /5
                  </div>
                </div>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
                <div className="h-full rounded-full bg-[#4dd28b]" style={{ width: `${stakeAlignedPct}%` }} />
              </div>
              <p className="mono text-[10px] text-[var(--color-xk-text-subtle)]">
                Sign-off del stakeholder del venue post-evento ("¿coincidió con el brief?").
              </p>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
      <div className="font-display text-xl font-extrabold" style={{ color: tone ?? 'var(--color-xk-lavender)' }}>
        {value}
      </div>
      <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">{label}</div>
    </div>
  );
}

function Panel({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4">
      <div className="flex items-center gap-2">
        <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">{title}</h2>
        {sub && <span className="mono text-[10px] text-[var(--color-xk-accent)]">· {sub}</span>}
      </div>
      {children}
    </div>
  );
}
